import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiService } from '../../services/adminApi';
import { SectionFilterBar, SortOrder, TableCard, TableHeader, TableViewport, usePageSize } from '../table/TableKit';
import {
  EmptyRow,
  ErrorNotice,
  LoadingSpinner,
  SortableTh,
  instanceLabel,
  selectClass,
  tdClass,
  thClass,
} from './shared';
import RelativeTime from '../RelativeTime';
import { urlEnum, urlPositiveInt, urlString, useUrlState } from '../../hooks/useUrlState';
import { useSearchDraft } from '../../hooks/useSearchDraft';
import { usePageClamp } from '../../hooks/usePageClamp';

const URL_SPEC = {
  type: urlEnum(['', 'converter', 'analyzer_plus', 'help'] as const, ''),
  q: urlString(),
  sort: urlEnum(['created_at', 'request_type', 'email', 'instance_name'] as const, 'created_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

const TYPE_LABELS: Record<string, string> = {
  converter: 'Converter',
  analyzer_plus: 'Analyzer+',
  help: 'Help',
};

function AdminContactRequests() {
  const [pageSize, setPageSize] = usePageSize('admin-contact-requests');
  const [urlState, setUrlState] = useUrlState(URL_SPEC);
  const [searchInput, setSearchInput] = useSearchDraft(urlState.q, (q) => setUrlState({ q, page: 1 }));
  const offset = (urlState.page - 1) * pageSize;
  const onSort = (sort: string, order: SortOrder) => setUrlState({
    sort: sort as typeof urlState.sort,
    order,
    page: 1,
  });

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset,
      request_type: urlState.type || undefined,
      search: urlState.q || undefined,
      sort: urlState.sort,
      order: urlState.order,
    }),
    [pageSize, offset, urlState.type, urlState.q, urlState.sort, urlState.order],
  );

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['admin', 'contactRequests', params],
    queryFn: () => adminApiService.getContactRequests(params),
    placeholderData: (prev) => prev,
    retry: false,
  });

  usePageClamp({
    page: urlState.page,
    pageSize,
    total: data?.total,
    ready: !isLoading && !isPlaceholderData && !!data,
    setPage: (page) => setUrlState({ page }),
  });

  return (
    <div>
      {error && <ErrorNotice message="Failed to load contact requests." />}
      <SectionFilterBar>
        <select
          aria-label="Request type filter"
          className={selectClass}
          value={urlState.type}
          onChange={(e) => setUrlState({ type: e.target.value as typeof urlState.type, page: 1 })}
        >
          <option value="">All Types</option>
          <option value="converter">Converter</option>
          <option value="analyzer_plus">Analyzer+</option>
          <option value="help">Help</option>
        </select>
      </SectionFilterBar>
      <TableCard>
        <TableHeader
          title="Contact Requests"
          search={{
            value: searchInput,
            onChange: setSearchInput,
            ariaLabel: 'Search contact requests',
          }}
          pagination={{
            offset,
            limit: pageSize,
            total: data?.total ?? 0,
            onOffsetChange: (nextOffset) => setUrlState({ page: nextOffset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setUrlState({ page: 1 });
            },
          }}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <TableViewport>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <SortableTh label="Time" sortKey="created_at" sort={urlState.sort} order={urlState.order} onSort={onSort} />
                <SortableTh label="Type" sortKey="request_type" sort={urlState.sort} order={urlState.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Email" sortKey="email" sort={urlState.sort} order={urlState.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Instance" sortKey="instance_name" sort={urlState.sort} order={urlState.order} onSort={onSort} firstOrder="asc" />
                <th className={thClass}>Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(data?.items ?? []).map((req) => (
                <tr key={req.id}>
                  <td className={`${tdClass} whitespace-nowrap`}><RelativeTime value={req.created_at} /></td>
                  <td className={tdClass}>
                    <span className="bg-gray-600/30 text-gray-300 px-2 py-0.5 rounded text-xs">
                      {TYPE_LABELS[req.request_type] || req.request_type}
                    </span>
                  </td>
                  <td className={tdClass}>{req.email}</td>
                  <td className={tdClass}>{instanceLabel(req.instance_name, req.instance_key)}</td>
                  <td className={`${tdClass} max-w-xl whitespace-pre-wrap`}>{req.note}</td>
                </tr>
              ))}
              {(data?.items ?? []).length === 0 && <EmptyRow colSpan={5} text="No contact requests found." />}
            </tbody>
          </table>
          </TableViewport>
        )}
      </TableCard>
    </div>
  );
}

export default AdminContactRequests;
